import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import BillingForm from "../components/BillingForm";
import { getMetaTrackingData } from "../utils/tracking";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  Star,
  Zap,
  Info,
  Shield,
  CreditCard,
  ChevronDown,
  CheckCircle,
} from "lucide-react";
import { clsx } from "clsx";
import { track } from "../utils/track";
import { twMerge } from "tailwind-merge";

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const planOrder = ["free", "basic", "standard", "business", "enterprise"];

const getMonthlyEquivalent = (plan, cycle) => {
  if (!plan) return 0;
  return cycle === "monthly"
    ? plan.monthly_price_eur
    : Math.round(plan.yearly_price_eur / 12);
};

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [billingCycle, setBillingCycle] = useState("yearly");
  const [pricingData, setPricingData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [pendingCreditPack, setPendingCreditPack] = useState(null);
  const [selectedPlanKey, setSelectedPlanKey] = useState(null);
  const [selectedPlanLabel, setSelectedPlanLabel] = useState("");
  const [selectedPlanPrice, setSelectedPlanPrice] = useState(0);
  const [showLoseTokensModal, setShowLoseTokensModal] = useState(false);
  const [pendingPlanToConfirm, setPendingPlanToConfirm] = useState(null);

  const language = i18n.language || "es";
  const currentPlan =
    subscriptionData?.subscription_plan || user?.subscription_plan || "free";

  const subscriptionStatus = subscriptionData?.subscription_status || null;
  const isCancelingSubscription = subscriptionStatus === "canceling";
  const cancelEffectiveAt = subscriptionData?.next_billing_date || null;
  const formatDateShort = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat(language === "es" ? "es-ES" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(d);
  };

  const monthlyLimit = Number(subscriptionData?.monthly_credit_limit || 0);
  const usedThisMonth = Number(subscriptionData?.credits_used_this_month || 0);
  const monthlyRemaining = Math.max(0, monthlyLimit - usedThisMonth);

  // Check if user can change plan immediately
  const hasActivePaidPlan =
    currentPlan &&
    currentPlan !== "free" &&
    (subscriptionData?.subscription_status === "active" ||
      subscriptionData?.subscription_status === "canceling");
  const canChangePlan = true;

  // Determine if selected plan is an upgrade/downgrade
  const isUpgrade =
    selectedPlanKey &&
    pricingData?.plans[selectedPlanKey] &&
    planOrder.indexOf(selectedPlanKey) > planOrder.indexOf(currentPlan);
  const isPlanChange =
    selectedPlanKey && currentPlan !== selectedPlanKey && hasActivePaidPlan;

  useEffect(() => {
    const loadData = async () => {
      const fallback = {
        plans: {
          free: {
            name: { es: "Free", en: "Free" },
            description: { es: "Plan gratuito", en: "Free plan" },
            credits_per_month: 20,
            monthly_price_eur: 0,
            yearly_price_eur: 0,
            stripe_price_ids: { monthly: null, yearly: null },
            features: {
              max_scanners: 10,
              team_members: 1,
              support_level: "Basic",
            },
          },
          basic: {
            name: { es: "Basic", en: "Basic" },
            description: {
              es: "Extracción automática con Acceso API Profesional incluido",
              en: "Automated extraction with Professional API Access included",
            },
            credits_per_month: 200,
            monthly_price_eur: 15,
            yearly_price_eur: 150,
            cost_per_credit: 0.075,
            stripe_price_ids: {
              monthly: "price_1SkMkXBP2yXjBIwfzGaebVJt",
              yearly: "price_1SkMkmBP2yXjBIwfkNuQCPjb",
            },
            features: {
              unlimited_scanners: true,
              team_members: 2,
              support_level: "Priority",
              api_access: true,
            },
          },
          standard: {
            name: { es: "Plan Standard", en: "Standard Plan" },
            description: {
              es: "Para PYMES. Incluye Acceso API Completo para integraciones",
              en: "For SMEs. Full API Access for integrations included",
            },
            credits_per_month: 1000,
            monthly_price_eur: 45,
            yearly_price_eur: 450,
            cost_per_credit: 0.045,
            stripe_price_ids: {
              monthly: "price_1SkMkXBP2yXjBIwfVgNUwchs",
              yearly: "price_1SkMkmBP2yXjBIwfmSbUjxNn",
            },
            features: {
              unlimited_scanners: true,
              team_members: 5,
              support_level: "Priority",
              api_access: true,
            },
          },
          business: {
            name: { es: "Business", en: "Business" },
            description: {
              es: "Solución completa para empresas con Acceso API de alto volumen",
              en: "Complete solution for businesses with high-volume API Access",
            },
            credits_per_month: 3500,
            monthly_price_eur: 95,
            yearly_price_eur: 950,
            cost_per_credit: 0.027,
            stripe_price_ids: {
              monthly: "price_1SkMkXBP2yXjBIwfDtL3nL01",
              yearly: "price_1SkMkmBP2yXjBIwf9kkWv8Vm",
            },
            features: {
              unlimited_scanners: true,
              team_members: 20,
              support_level: "Priority",
              api_access: true,
              custom_scanners: true,
            },
          },
          enterprise: {
            name: { es: "Enterprise", en: "Enterprise" },
            description: {
              es: "Máximo volumen y soporte con Acceso API Ilimitado",
              en: "Maximum volume and support with Unlimited API Access",
            },
            credits_per_month: 15000,
            monthly_price_eur: 195,
            yearly_price_eur: 1950,
            cost_per_credit: 0.013,
            stripe_price_ids: {
              monthly: "price_1SkMkXBP2yXjBIwfmGK1fgxV",
              yearly: "price_1SkMkmBP2yXjBIwfzfiaM8qF",
            },
            features: {
              unlimited_scanners: true,
              team_members: -1,
              support_level: "Dedicated",
              api_access: true,
              custom_scanners: true,
              bulk_processing: true,
            },
          },
        },
        additional_features: { credit_packs: {} },
      };

      try {
        let pricingResponse = null;
        try {
          pricingResponse = await api.getSubscriptionPricing();
        } catch (err) {
          // If pricing fetch fails, we'll set a fallback later but continue to fetch subscription
          console.error("Pricing fetch failed, will use fallback", err);
        }

        const subscriptionResponse = await api
          .getSubscription()
          .catch(() => null);

        // If fetch failed, pricingResponse will be null and we'll set a fallback below
        const resolvedPricing = pricingResponse || fallback;
        setPricingData(resolvedPricing);

        // axiosClient returns the lambda payload. Most endpoints wrap data under `subscription`.
        setSubscriptionData(
          subscriptionResponse?.subscription || subscriptionResponse
        );

        // Pre-select logic (use resolvedPricing which falls back when fetch failed)
        const currentPlanFromAPI =
          subscriptionResponse?.subscription?.subscription_plan ||
          subscriptionResponse?.subscription_plan;
        const currentPlanFromContext = user?.subscription_plan;

        // Default to 'standard' if no current plan, or the current plan
        const defaultKey =
          currentPlanFromAPI || currentPlanFromContext || "standard";

        if (resolvedPricing.plans[defaultKey]) {
          setSelectedPlanKey(defaultKey);
        } else {
          const firstAvailable = planOrder.find(
            (k) => resolvedPricing.plans[k]
          );
          if (firstAvailable) setSelectedPlanKey(firstAvailable);
        }
      } catch (err) {
        console.error("Error loading pricing data:", err);
        setError(t("pricing.errorLoading", "Error loading pricing data"));
        // Ensure we have at least the previously prepared fallback available
        setPricingData((prev) => prev || fallback);
      }
    };
    loadData();
  }, [t, user]);

  // Ensure we always have a selected plan after pricingData loads
  useEffect(() => {
    if (!pricingData || selectedPlanKey) return;
    const firstAvailable = planOrder.find((k) => pricingData.plans[k]);
    if (firstAvailable) setSelectedPlanKey(firstAvailable);
  }, [pricingData, selectedPlanKey]);

  useEffect(() => {
    track("pricing_view");
  }, []);

  useEffect(() => {
    // Params handling for callbacks
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    const canceled = urlParams.get("canceled");

    if (sessionId) {
      track("checkout_return", { result: "success", kind: "plan" });
      setSuccess(
        t("pricing.subscriptionSuccess", "Subsctiption activated successfully!")
      );
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled) {
      track("checkout_return", { result: "canceled", kind: "plan" });
      setError(t("pricing.paymentCanceled", "Payment canceled."));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [t]);

  useEffect(() => {
    if (!pricingData || !selectedPlanKey) return;
    const plan = pricingData.plans[selectedPlanKey];
    if (!plan) return;

    setSelectedPlanLabel(plan?.name?.[language] || plan?.name?.en || "");
    setSelectedPlanPrice(getMonthlyEquivalent(plan, billingCycle));
  }, [billingCycle, language, pricingData, selectedPlanKey]);

  const selectPlan = (planKey) => {
    track("plan_select", { item: planKey, cycle: billingCycle, via: "card" });
    if (!pricingData) return;
    const plan = pricingData.plans[planKey];
    if (!plan) return;

    setSelectedPlanKey(planKey);
    setSelectedPlanLabel(plan?.name?.[language] || plan?.name?.en || "");
    setSelectedPlanPrice(getMonthlyEquivalent(plan, billingCycle));
  };

  const handlePlanSelect = async (planId) => {
    track("plan_select", { item: planId, cycle: billingCycle, via: "button" });
    if (!user) {
      navigate("/login");
      return;
    }

    setError(null);
    setLoadingPlan(planId);

    try {
      if (planId === "free") {
        // Downgrades to free are handled ONLY via Stripe Billing Portal cancellation.
        // The actual plan change happens via Stripe webhook at end of billing period.
        const { portalUrl } = await api.getCustomerPortalUrl();
        window.location.href = portalUrl;
        return;
      }

      // Plan changes between paid plans must go through Stripe Checkout (paid) so the user
      // pays the new plan and we cancel the previous subscription server-side via webhook.

      const plan = pricingData?.plans?.[planId];
      if (!plan) throw new Error("Plan not available");

      setSelectedPlanKey(planId);
      setSelectedPlanLabel(plan.name[language] || plan.name.en);
      setSelectedPlanPrice(getMonthlyEquivalent(plan, billingCycle));

      // If switching plans and there are remaining monthly tokens, show confirmation modal
      const switchingPlan = planId !== currentPlan && hasActivePaidPlan;
      if (switchingPlan && monthlyRemaining > 0) {
        setPendingPlanToConfirm(planId);
        setShowLoseTokensModal(true);
        setLoadingPlan(null);
        return;
      }

      const stripeCustomerId = subscriptionData?.stripe_customer_id || null;
      if (stripeCustomerId) {
        await proceedToCheckoutFromBilling({ planId });
      } else {
        setShowBillingForm(true);
      }
    } catch (err) {
      console.error("Error selecting plan:", err);
      setError(
        err.response?.data?.message ||
          t("pricing.errorSelectingPlan", "Error selecting plan")
      );
    } finally {
      setLoadingPlan(null);
    }
  };

  const proceedToCheckoutFromBilling = async ({ planId } = {}) => {
    const effectivePlanKey = planId || selectedPlanKey;
    if (!effectivePlanKey || !pricingData) return;
    try {
      const plan = pricingData.plans[effectivePlanKey];
      const priceId =
        billingCycle === "monthly"
          ? plan.stripe_price_ids.monthly
          : plan.stripe_price_ids.yearly;

      const replaceExistingSubscription =
        hasActivePaidPlan &&
        effectivePlanKey !== "free" &&
        effectivePlanKey !== currentPlan;

      const tracking = getMetaTrackingData();

      const { checkoutUrl } = await api.createCheckoutSession({
        priceId,
        planId: effectivePlanKey,
        billingCycle,
        replaceExistingSubscription,
        successUrl: `${window.location.origin}/pricing?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/pricing?canceled=true`,
        ...tracking,
      });
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error("Error creating checkout", err);
      setError(err?.response?.data?.message || t("common.error", "Error"));
    } finally {
      setShowBillingForm(false);
      if (planId) setSelectedPlanKey(null);
    }
  };

  const handleConfirmLoseTokens = async () => {
    if (!pendingPlanToConfirm) return;
    setShowLoseTokensModal(false);
    setLoadingPlan(pendingPlanToConfirm);
    const stripeCustomerId = subscriptionData?.stripe_customer_id || null;
    if (stripeCustomerId) {
      await proceedToCheckoutFromBilling({ planId: pendingPlanToConfirm });
    } else {
      // Show billing form to collect payment method and proceed
      setShowBillingForm(true);
    }
    setPendingPlanToConfirm(null);
    setLoadingPlan(null);
  };

  const handleCancelLoseTokens = () => {
    setShowLoseTokensModal(false);
    setPendingPlanToConfirm(null);
  };

  const handleBuyCreditPack = async (packKey) => {
    if (!user) {
      navigate("/login");
      return;
    }
    setError(null);
    setLoadingPlan(`pack_${packKey}`);
    try {
      const stripeCustomerId = subscriptionData?.stripe_customer_id || null;
      if (!stripeCustomerId) {
        setPendingCreditPack(packKey);
        setShowBillingForm(true);
        return;
      }
      const tracking = getMetaTrackingData();
      const { checkoutUrl } = await api.createCreditPackCheckout({
        packId: packKey,
        successUrl: `${window.location.origin}/tokens?purchase=success`,
        cancelUrl: `${window.location.origin}/pricing?canceled=true`,
        ...tracking,
      });
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error("Error purchasing pack:", err);
      setError(
        err.response?.data?.message ||
          t("pricing.errorPurchasing", "Error purchasing")
      );
    } finally {
      setLoadingPlan(null);
    }
  };

  const selectedPlan = selectedPlanKey
    ? pricingData?.plans?.[selectedPlanKey]
    : null;

  if (!pricingData) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Filter plans to show relevant ones (simplified list)
  const displayPlans = ["free", "basic", "standard", "business", "enterprise"];
  const availablePlans = planOrder.filter((k) => !!pricingData.plans[k]);

  return (
    <div className="min-h-screen bg-gray-50 pb-20 overflow-x-hidden">
      {/* Hero Section with Ambient Background */}
      <div className="relative pt-20 pb-16 px-4 text-center overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-gradient-to-b from-primary-50/50 to-transparent pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-primary-200/20 rounded-full blur-[100px] pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 max-w-3xl mx-auto"
        >
          <span className="inline-block py-1 px-3 rounded-full bg-primary-100 text-primary-600 text-xs font-bold tracking-wider uppercase mb-4">
            {t("pricing.badge", "Flexible Pricing")}
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight">
            {t("pricing.title", "Choose the perfect plan for your business")}
          </h1>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed">
            {t(
              "pricing.subtitle",
              "Start with free credits. Upgrade anytime to unlock more power."
            )}
          </p>

          {/* Toggle */}
          <div className="inline-flex bg-white p-1.5 rounded-full shadow-sm border border-gray-200 relative mb-12">
            <button
              onClick={() => {
                track("billing_cycle", { cycle: "monthly" });
                setBillingCycle("monthly");
              }}
              className={cn(
                "px-6 py-2.5 rounded-full text-sm font-bold transition-all relative z-10",
                billingCycle === "monthly"
                  ? "text-white"
                  : "text-gray-600 hover:text-gray-900"
              )}
            >
              {t("pricing.monthly", "Monthly")}
            </button>
            <button
              onClick={() => {
                track("billing_cycle", { cycle: "yearly" });
                setBillingCycle("yearly");
              }}
              className={cn(
                "px-6 py-2.5 rounded-full text-sm font-bold transition-all relative z-10 flex items-center gap-2",
                billingCycle === "yearly"
                  ? "text-white"
                  : "text-gray-600 hover:text-gray-900"
              )}
            >
              {t("pricing.yearly", "Yearly")}
              <span
                className={cn(
                  "text-[10px] px-1.5 py-0.5 rounded-full font-bold",
                  billingCycle === "yearly"
                    ? "bg-white/20 text-white"
                    : "bg-green-100 text-green-700"
                )}
              >
                -20%
              </span>
            </button>

            <motion.div
              className="absolute top-1.5 bottom-1.5 bg-primary-600 rounded-full shadow-md z-0"
              initial={false}
              animate={{
                x: billingCycle === "monthly" ? 0 : "100%",
                width: "50%", // Rough approximation, adjusting via layoutId/layout usually better but simple translation works for fixed width
              }}
              style={{ left: 6, width: "calc(50% - 6px)" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
          </div>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-red-50 text-red-700 rounded-xl border border-red-200 flex items-center gap-3"
          >
            <Info className="w-5 h-5" /> {error}{" "}
            <button
              onClick={() => setError(null)}
              className="ml-auto font-bold"
            >
              &times;
            </button>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-green-50 text-green-700 rounded-xl border border-green-200 flex items-center gap-3"
          >
            <CheckCircle className="w-5 h-5" /> {success}{" "}
            <button
              onClick={() => setSuccess(null)}
              className="ml-auto font-bold"
            >
              &times;
            </button>
          </motion.div>
        )}

        {/* Layout: Grid for Desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Sidebar Plan Selector */}
          <div className="lg:col-span-4 sticky top-8 space-y-6">
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl border border-white/60 shadow-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 px-2">
                {t("pricing.selectLevel", "Select a Plan Level")}
              </h3>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
                {availablePlans.map((planKey) => {
                  const plan = pricingData.plans[planKey];
                  const isSelected = selectedPlanKey === planKey;
                  const isCurrent = currentPlan === planKey;
                  const monthlyPrice = getMonthlyEquivalent(plan, billingCycle);

                  return (
                    <button
                      key={planKey}
                      onClick={() => selectPlan(planKey)}
                      className={cn(
                        "w-full text-left p-4 rounded-xl border transition-all duration-200 group relative overflow-hidden",
                        isSelected
                          ? "border-primary-500 bg-primary-50/50 shadow-md transform scale-[1.02]"
                          : "border-gray-100 hover:border-primary-200 hover:bg-gray-50"
                      )}
                    >
                      <div className="flex justify-between items-center relative z-10">
                        <div>
                          <span
                            className={cn(
                              "font-bold block",
                              isSelected ? "text-primary-700" : "text-gray-700"
                            )}
                          >
                            {plan.name[language] || plan.name.en}
                          </span>
                          {isCurrent && (
                            <span
                              title={
                                isCancelingSubscription && cancelEffectiveAt
                                  ? `${t(
                                      "pricing.status.canceling",
                                      "Canceling"
                                    )} · ${t(
                                      "pricing.status.activeUntil",
                                      "Active until"
                                    )} ${
                                      formatDateShort(cancelEffectiveAt) || ""
                                    }`
                                  : undefined
                              }
                              className={cn(
                                "inline-flex items-center mt-2 px-2 py-0.5 rounded-full text-[11px] font-bold border",
                                isCancelingSubscription
                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : "bg-primary-50 text-primary-700 border-primary-200"
                              )}
                            >
                              {t("pricing.currentPlanShort", "Current")} ·{" "}
                              {isCancelingSubscription
                                ? t("pricing.status.canceling", "Canceling")
                                : t("pricing.status.active", "Active")}
                            </span>
                          )}
                          <span className="block text-xs text-gray-500 mt-1">
                            {plan.credits_per_month?.toLocaleString()}{" "}
                            {t("pricing.credits", "credits")}
                          </span>
                        </div>
                        <div className="text-right">
                          <div
                            className={cn(
                              "font-bold",
                              isSelected ? "text-primary-700" : "text-gray-900"
                            )}
                          >
                            €{monthlyPrice}
                          </div>
                          <div className="text-[10px] text-gray-400">/mo</div>
                        </div>
                      </div>
                      {plan.popular && (
                        <div
                          className="absolute top-0 right-0 w-3 h-3 bg-yellow-400 rounded-bl-lg shadow-sm"
                          title="Popular"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl shadow-xl p-6 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 p-16 bg-primary-500/20 rounded-full blur-2xl -mr-10 -mt-10" />
              <h3 className="text-lg font-bold mb-2 relative z-10">
                {t("pricing.needCustomVolume", "Need Custom Volume?")}
              </h3>
              <p className="text-gray-300 text-sm mb-4 relative z-10">
                {t(
                  "pricing.customVolumeDesc",
                  "Processing millions of documents? We offer enterprise discounts and dedicated infrastructure."
                )}
              </p>
              <button className="w-full py-2 bg-white/10 hover:bg-white/20 rounded-xl font-bold text-sm transition-colors border border-white/10 relative z-10">
                {t("pricing.contactSales", "Contact Sales")}
              </button>
            </div>
          </div>

          {/* Main Content: Plan Detail Card */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              {selectedPlan && (
                <motion.div
                  key={selectedPlanKey}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-[2rem] shadow-2xl border border-gray-100 overflow-hidden relative"
                >
                  {/* Header Band */}
                  <div className="bg-gray-50/50 border-b border-gray-100 p-8 sm:p-10 relative">
                    {selectedPlan.popular && (
                      <div className="absolute top-6 right-6 flex items-center gap-1 bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                        <Star className="w-3 h-3 fill-yellow-700" />{" "}
                        {t("pricing.mostPopular", "Most Popular")}
                      </div>
                    )}

                    <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 relative z-10">
                      <div>
                        <h2 className="text-4xl font-extrabold text-gray-900 mb-2">
                          {selectedPlan?.name?.[language] ||
                            selectedPlan?.name?.en}
                        </h2>
                        <p className="text-lg text-gray-500 max-w-md">
                          {selectedPlan?.description?.[language] ||
                            selectedPlan?.description?.en ||
                            ""}
                        </p>
                      </div>
                      <div className="text-left md:text-right">
                        <div className="flex items-baseline gap-1">
                          <span className="text-5xl font-extrabold text-gray-900 tracking-tighter">
                            €{getMonthlyEquivalent(selectedPlan, billingCycle)}
                          </span>
                          <span className="text-gray-400 font-medium text-lg">
                            /mo
                          </span>
                        </div>
                        {billingCycle === "yearly" && (
                          <div className="text-sm text-green-600 font-bold mt-1">
                            Billed €{selectedPlan.yearly_price_eur} yearly
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Content Body */}
                  <div className="p-8 sm:p-10">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                      <div className="bg-primary-50/50 rounded-2xl p-6 border border-primary-100">
                        <div className="text-xs font-bold text-primary-600 uppercase tracking-wider mb-2 flex items-center gap-2">
                          <Zap className="w-4 h-4" />{" "}
                          {selectedPlanKey === "free"
                            ? t("pricing.creditsLabel", "Credits")
                            : t("pricing.monthlyCredits", "Monthly Credits")}
                        </div>
                        <div className="text-3xl font-extrabold text-gray-900">
                          {selectedPlan.credits_per_month?.toLocaleString()}
                        </div>
                        {selectedPlanKey === "free" && (
                          <div className="text-sm text-gray-500 mt-1">
                            {t(
                              "pricing.oneTimeGrant",
                              "Renews every month"
                            )}
                          </div>
                        )}
                        {selectedPlan.cost_per_credit && (
                          <div className="text-sm text-gray-500 mt-1">
                            €{selectedPlan.cost_per_credit.toFixed(3)} per
                            credit
                          </div>
                        )}
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                        <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                          <Shield className="w-4 h-4" /> Credit Rollover
                        </div>
                        <div className="text-lg font-bold text-gray-900">
                          {selectedPlanKey === "free"
                            ? t(
                                "pricing.oneTimeGrant",
                                "Renews every month"
                              )
                            : selectedPlan.features?.credit_reset
                            ? t("pricing.monthlyReset", "Resets Monthly")
                            : t("pricing.contactSales", "Contact Sales")}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {selectedPlanKey === "free"
                            ? t(
                                "pricing.oneTimeHint",
                                "The free plan includes 20 pages a month; they renew monthly and don't carry over."
                              )
                            : t(
                                "pricing.creditUseHint",
                                "Use them or lose them"
                              )}
                        </div>
                      </div>
                    </div>

                    <hr className="border-gray-100 mb-10" />

                    <div className="mb-10">
                      <h4 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-gray-400" />{" "}
                        {t("pricing.planFeatures", "Plan Features")}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
                        {/* Generate features list based on plan details */}
                        {[
                          selectedPlan.features?.unlimited_scanners
                            ? t(
                                "pricing.unlimitedScanners",
                                "Unlimited Scanners"
                              )
                            : t("pricing.maxTemplates", {
                                count: selectedPlan.features?.max_scanners,
                              }),
                          selectedPlan.features?.team_members === -1
                            ? t(
                                "pricing.unlimitedMembers",
                                "Unlimited Team Members"
                              )
                            : `${
                                selectedPlan.features?.team_members || 1
                              } Team Member${
                                (selectedPlan.features?.team_members || 1) > 1
                                  ? "s"
                                  : ""
                              }`,
                          selectedPlan.features?.api_access &&
                            t("pricing.apiAccess", "Full API Access"),
                          selectedPlan.features?.advanced_postprocessing &&
                            t(
                              "pricing.features.advancedPostprocessing",
                              "Advanced Post-processing"
                            ),
                          selectedPlan.features?.custom_scanners &&
                            t(
                              "pricing.features.customScanners",
                              "Custom Scanner Logic"
                            ),
                          t("pricing.features.support", "Support: {{level}}", {
                            level:
                              selectedPlan.features?.support_level ||
                              "Standard",
                          }),
                        ]
                          .filter(Boolean)
                          .map((feature, i) => {
                            const isApiAccess = feature === t("pricing.apiAccess", "Full API Access");
                            return (
                              <div
                                key={i}
                                className={cn(
                                  "flex items-start gap-3 p-2 rounded-xl transition-colors",
                                  isApiAccess ? "bg-amber-50/50 border border-amber-100" : ""
                                )}
                              >
                                <div
                                  className={cn(
                                    "mt-0.5 p-1 rounded-full",
                                    isApiAccess
                                      ? "bg-amber-100 text-amber-600"
                                      : "bg-green-100 text-green-600"
                                  )}
                                >
                                  {isApiAccess ? (
                                    <Zap className="w-3 h-3 fill-amber-600" />
                                  ) : (
                                    <Check className="w-3 h-3" />
                                  )}
                                </div>
                                <span
                                  className={cn(
                                    "text-gray-700 font-medium",
                                    isApiAccess ? "text-gray-900 font-bold flex items-center gap-2" : ""
                                  )}
                                >
                                  {feature}
                                  {isApiAccess && (
                                    <span className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wider font-extrabold animate-pulse">
                                      {t("common.new", "NEW")}
                                    </span>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    <button
                      onClick={() => handlePlanSelect(selectedPlanKey)}
                      disabled={
                        loadingPlan === selectedPlanKey ||
                        currentPlan === selectedPlanKey ||
                        (selectedPlanKey === "free" && currentPlan === "free")
                      }
                      className={cn(
                        "w-full py-4 rounded-xl font-bold text-lg shadow-xl shadow-primary-500/10 transition-all transform active:scale-[0.98]",
                        currentPlan === selectedPlanKey ||
                          (selectedPlanKey === "free" && currentPlan === "free")
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                          : "bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 hover:shadow-primary-500/25"
                      )}
                    >
                      {loadingPlan === selectedPlanKey
                        ? t("pricing.processing", "Processing...")
                        : currentPlan === selectedPlanKey
                        ? t("pricing.currentPlan", "Current Plan")
                        : selectedPlanKey === "free"
                        ? t("pricing.switchToFree", "Switch to Free")
                        : isUpgrade
                        ? t("pricing.upgradePlanBtn", "Upgrade Plan")
                        : t("pricing.selectPlanBtn", "Select Plan")}
                    </button>

                    {hasActivePaidPlan &&
                      selectedPlanKey &&
                      selectedPlanKey !== "free" &&
                      selectedPlanKey !== currentPlan &&
                      monthlyRemaining > 0 && (
                        <p className="mt-4 text-center text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-100">
                          {t(
                            "pricing.planChangeCreditWarning",
                            "Changing plan resets monthly credits. You will lose {{monthlyRemaining}} unused monthly credits. Credit packs are not affected.",
                            { monthlyRemaining }
                          )}
                        </p>
                      )}

                    {!user && (
                      <div className="text-center mt-4 text-sm text-gray-500">
                        {t(
                          "pricing.loginPrompt",
                          "You'll be asked to create an account first"
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Credit packs have been removed from the Pricing page (moved to Tokens page). */}
      </div>

      {showBillingForm && (
        <BillingForm
          open={showBillingForm}
          onClose={() => setShowBillingForm(false)}
          planLabel={selectedPlanLabel}
          planPrice={selectedPlanPrice}
          billingCycle={billingCycle}
          initialData={subscriptionData || {}}
          onProceedToCheckout={async () => {
            if (pendingCreditPack) {
              setShowBillingForm(false);
              try {
                const { checkoutUrl } = await api.createCreditPackCheckout({
                  packId: pendingCreditPack,
                  successUrl: `${window.location.origin}/tokens?purchase=success`,
                  cancelUrl: `${window.location.origin}/pricing?canceled=true`,
                });
                window.location.href = checkoutUrl;
              } catch (err) {
                console.error("Error pack checkout", err);
                setError(err?.response?.data?.message || "Error");
              } finally {
                setPendingCreditPack(null);
              }
            } else {
              await proceedToCheckoutFromBilling({});
            }
          }}
        />
      )}
      {showLoseTokensModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 mx-4">
            <h3 className="text-lg font-bold mb-2">
              {t("pricing.confirmLoseTokens.title", "Tienes tokens sin usar")}
            </h3>
            <p className="text-sm text-gray-700 mb-4">
              {t(
                "pricing.confirmLoseTokens.message",
                "Te quedan {{count}} tokens mensuales. Se perderán si cambias de plan. Gástalos antes si no quieres perderlos.",
                { count: monthlyRemaining }
              )}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleConfirmLoseTokens}
                className="bg-red-600 text-white px-4 py-2 rounded font-bold"
              >
                {t(
                  "pricing.confirmLoseTokens.changeAndLose",
                  "Cambiar y perder {{count}} tokens",
                  { count: monthlyRemaining }
                )}
              </button>
              <button
                onClick={handleCancelLoseTokens}
                className="bg-gray-100 text-gray-800 px-4 py-2 rounded font-medium"
              >
                {t("pricing.confirmLoseTokens.goBack", "Volver")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showLoseTokensModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.18 }}
            className="max-w-lg w-full bg-white/80 backdrop-blur-xl border border-white/60 rounded-2xl shadow-lg p-6 mx-auto"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-xl bg-primary-50 text-primary-600 inline-flex items-center justify-center">
                  <Info className="w-6 h-6" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-extrabold text-gray-900">
                      {t(
                        "pricing.confirmLoseTokens.title",
                        "Tienes tokens sin usar"
                      )}
                    </h3>
                    <p className="text-sm text-gray-700 mt-1">
                      {t(
                        "pricing.confirmLoseTokens.message",
                        "Te quedan {{count}} tokens mensuales. Se perderán si cambias de plan. Gástalos antes si no quieres perderlos.",
                        { count: monthlyRemaining }
                      )}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      {t("pricing.confirmLoseTokens.note_free")}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-3">
                  <button
                    onClick={handleConfirmLoseTokens}
                    className="rounded-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 font-bold shadow-md transition-colors"
                  >
                    {t(
                      "pricing.confirmLoseTokens.changeAndLose",
                      "Cambiar y perder {{count}} tokens",
                      { count: monthlyRemaining }
                    )}
                  </button>
                  <button
                    onClick={handleCancelLoseTokens}
                    className="rounded-full bg-white/90 border border-gray-200 text-gray-800 px-4 py-2 font-medium hover:bg-white"
                  >
                    {t("pricing.confirmLoseTokens.goBack", "Volver")}
                  </button>
                </div>

                <p className="mt-3 text-xs text-gray-500">
                  {t(
                    "pricing.planChangeCreditWarning",
                    "Changing plan resets monthly credits. You will lose {{monthlyRemaining}} unused monthly credits. Credit packs are not affected.",
                    { monthlyRemaining }
                  )}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
